import React, { useState, useEffect, useRef } from 'react';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService';


// Add this component before your Booking component

// CCAvenue Affordability Widget Component
const CCAvenueWidget = ({ amount }) => {
  const widgetRef = useRef(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);

  useEffect(() => {
    // Load the CCAvenue widget script
    const script = document.createElement('script');
    script.src = 'https://secure.ccavenue.com/scripts/affordability-widget/affordability-widget.js';
    script.async = true;
    script.onload = () => {
      console.log('CCAvenue widget script loaded');
      setIsScriptLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load CCAvenue widget script');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    // Initialize widget when script is loaded and amount is available
    if (isScriptLoaded && amount > 0) {
      const config = {
        "accessCode": "AVNA87NA77BW49ANWB",
        "amount": Math.round(amount), // Use the actual booking amount
        "showPlans": true,  // Force show plans
        "expanded": true     // Force expanded view (if supported)
      };
      
      setWidgetConfig(config);
      
      // Dispatch the config to the widget
      if (window.setWidgetConfig) {
        window.setWidgetConfig(config);
        console.log('CCAvenue widget initialized with amount:', amount);
      } else {
        console.log("setWidgetConfig() Not found - widget script may not be ready");
        // Retry after a short delay
        setTimeout(() => {
          if (window.setWidgetConfig) {
            window.setWidgetConfig(config);
            console.log('CCAvenue widget initialized (retry) with amount:', amount);
          }
        }, 500);
      }
    }
  }, [isScriptLoaded, amount]);

  // Update widget when amount changes (e.g., after promo code applied)
  useEffect(() => {
    if (isScriptLoaded && amount > 0 && window.setWidgetConfig) {
      const config = {
        "accessCode": "AVNA87NA77BW49ANWB",
        "amount": Math.round(amount)
      };
      window.setWidgetConfig(config);
      console.log('CCAvenue widget amount updated to:', amount);
    }
  }, [amount, isScriptLoaded]);

  return (
    <div className="mt-6">
      <div id="ccWidget" className="ccWidget"></div>
    </div>
  );
};

const Booking = ({ navigateTo }) => {
  const { state, dispatch, formatCurrency, calculateNights } = useBooking();
    // Add this helper function
  const isPromoStackableCheck = (promoCode) => {
    const availablePromoCodes = state.searchResults?.available_promo_codes;
    if (availablePromoCodes && promoCode) {
      const promoInfo = availablePromoCodes.find(p => p.code === promoCode);
      if (promoInfo && promoInfo.is_stackable === 1) {
        return true;
      }
    }
    return state.searchResults?.is_stackable === true;
  };
  const [isMobileSummaryOpen, setIsMobileSummaryOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false); // Add this
  const [redirectUrl, setRedirectUrl] = useState(''); // Add this to store URL
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [discountedTotals, setDiscountedTotals] = useState(null);
  const [promoCodeInfo, setPromoCodeInfo] = useState(null);
  const [roomDetails, setRoomDetails] = useState([]);
  const [loading, setLoading] = useState(false);
useEffect(() => {
  if (state.cart.length > 0) {
    console.log('📊 Cart Stackable Status:', state.cart.map(item => ({
      roomNumber: item.roomNumber,
      roomName: item.roomName,
      isStackableApplied: item.isStackableApplied,
      hasExistingOffer: item.hasExistingOffer,
      offerOverriddenByPromo: item.offerOverriddenByPromo,
      hasPromoApplied: item.hasPromoApplied
    })));
  }
}, [state.cart]);
  // Get user data from localStorage if logged in
  const getUserData = () => {
    const userData = sessionStorage.getItem('userData');
    const userToken = sessionStorage.getItem('userToken');
    
    if (userData && userToken) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  };

  // Initialize form data with user data if logged in
  const user = getUserData();
  
  const [formData, setFormData] = useState({
    title: user?.title || '',
    firstName: user?.name ? user.name.split(' ')[0] : '',
    lastName: user?.name ? user.name.split(' ').slice(1).join(' ') : '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.date_of_birth || user?.dateOfBirth || '',
    gstNumber: '',
    country: user?.country || '',
    city: user?.city || '',
    state: user?.state || '',
    pincode: user?.pincode || user?.zip_code || '',
    specialRequests: '',
    terms: false,
    cancellation: false
  });

  // Track if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(!!user);
  const [userId, setUserId] = useState(user?.id || null);
  const [idMstGuestDetailId, setIdMstGuestDetailId] = useState(user?.id_mst_guest_detail || null);

  // Form validity state
  const [isFormValid, setIsFormValid] = useState(false);

  // New state for dynamic dropdown data
  const [titles, setTitles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isLoadingTitles, setIsLoadingTitles] = useState(true);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);

  const nights = calculateNights();

  // Check if promo is from widget
  const isWidgetPromo = state.fromWidget && state.hasDiscount && state.promoCode;

  // Check if cart has any promo offers from widget
  const hasWidgetPromoOffer = () => {
    return state.cart.some(item => {
      if (item.ratePlan?.offer?.is_promo_code) {
        return true;
      }
      if (item.discountInfo?.is_promo_code) {
        return true;
      }
      return false;
    });
  };

  // Check if any non-promo offer exists
  const hasAnyNonPromoOfferApplied = () => {
    return state.cart.some(item => {
      const hasOffer = item.offerName || item.ratePlan?.offer || item.discountInfo;
      
      if (hasOffer && item.ratePlan?.offer?.is_promo_code) {
        return false;
      }
      if (hasOffer && item.discountInfo?.is_promo_code) {
        return false;
      }
      
      return hasOffer;
    });
  };

  // Check if promo can be applied
  const canApplyPromo = () => {
    if (isWidgetPromo) return false;
    if (hasAnyNonPromoOfferApplied()) return false;
    if (state.hasDiscount && !isWidgetPromo) return false;
    if (hasWidgetPromoOffer()) return false;
    return true;
  };

  // Calculate discount from widget promo if needed
  useEffect(() => {
    if (state.searchState.promo_code && !state.hasDiscount) {
      const hasPromoOffers = hasWidgetPromoOffer();
      
      if (hasPromoOffers) {
        const nights = calculateNights();
        let totalDiscount = 0;
        
        state.cart.forEach(item => {
          if (item.ratePlan?.offer?.is_promo_code) {
            const originalPrice = item.ratePlan.originalPrice || item.roomPrice / 0.9;
            const discountPerRoom = (originalPrice - item.roomPrice) * item.quantity * nights;
            totalDiscount += discountPerRoom;
          }
        });
        
        if (totalDiscount > 0) {
          dispatch({
            type: 'APPLY_WIDGET_PROMO',
            payload: {
              discountAmount: totalDiscount,
              promoCode: state.searchState.promo_code
            }
          });
          setPromoCode(state.searchState.promo_code);
        }
      }
    }
  }, [state.cart, state.searchState.promo_code, state.hasDiscount]);

  // Real-time validation effect
  useEffect(() => {
    const isValid = 
      formData.title.trim() !== '' &&
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.phone.trim() !== '' &&
      formData.country.trim() !== '' &&
      formData.terms === true &&
      formData.cancellation === true;

    setIsFormValid(isValid);
  }, [formData]);

  // Fetch titles from API
  useEffect(() => {
    const fetchTitles = async () => {
      try {
        setIsLoadingTitles(true);
        const response = await apiService.getTitles();
        
        if (response.status && response.data) {
          setTitles(response.data);
        } else {
          setTitles([
            { id: 1, name: 'Mr' },
            { id: 2, name: 'Mrs' },
            { id: 3, name: 'Ms' },
            { id: 4, name: 'Dr' },
            { id: 5, name: 'Prof' }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch titles:', error);
        setTitles([
          { id: 1, name: 'Mr' },
          { id: 2, name: 'Mrs' },
          { id: 3, name: 'Ms' },
          { id: 4, name: 'Dr' },
          { id: 5, name: 'Prof' }
        ]);
      } finally {
        setIsLoadingTitles(false);
      }
    };

    fetchTitles();
  }, []);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setIsLoadingCountries(true);
        const response = await apiService.getCountries();
        
        if (response.data) {
          const countryArray = Object.entries(response.data).map(([code, name]) => ({
            code,
            name
          }));
          setCountries(countryArray);
          
          if (!formData.country_code && formData.country === '') {
            const india = countryArray.find(c => c.code === 'IN');
            if (india) {
              setFormData(prev => ({
                ...prev,
                country: india.name,
                country_code: 'IN'
              }));
            }
          }
        } else {
          const fallbackCountries = [
            { code: 'IN', name: 'India' },
            { code: 'US', name: 'United States' },
            { code: 'GB', name: 'United Kingdom' },
            { code: 'CA', name: 'Canada' },
            { code: 'AU', name: 'Australia' },
            { code: 'DE', name: 'Germany' },
            { code: 'FR', name: 'France' },
            { code: 'JP', name: 'Japan' },
            { code: 'CN', name: 'China' },
            { code: 'SG', name: 'Singapore' },
            { code: 'AE', name: 'United Arab Emirates' }
          ];
          setCountries(fallbackCountries);
          
          if (!formData.country_code && formData.country === '') {
            setFormData(prev => ({
              ...prev,
              country: 'India',
              country_code: 'IN'
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        const fallbackCountries = [
          { code: 'IN', name: 'India' },
          { code: 'US', name: 'United States' },
          { code: 'GB', name: 'United Kingdom' },
          { code: 'CA', name: 'Canada' },
          { code: 'AU', name: 'Australia' },
          { code: 'DE', name: 'Germany' },
          { code: 'FR', name: 'France' },
          { code: 'JP', name: 'Japan' },
          { code: 'CN', name: 'China' },
          { code: 'SG', name: 'Singapore' },
          { code: 'AE', name: 'United Arab Emirates' }
        ];
        setCountries(fallbackCountries);
        
        if (!formData.country_code && formData.country === '') {
          setFormData(prev => ({
            ...prev,
            country: 'India',
            country_code: 'IN'
          }));
        }
      } finally {
        setIsLoadingCountries(false);
      }
    };

  fetchCountries();
}, []);

  // Check for user login status changes
  useEffect(() => {
    const checkUserLogin = () => {
      const userData = sessionStorage.getItem('userData');
      const userToken = sessionStorage.getItem('userToken');
      
      if (userData && userToken) {
        try {
          const parsedUser = JSON.parse(userData);
          setIsLoggedIn(true);
          setUserId(parsedUser.id);
          setIdMstGuestDetailId(parsedUser.id_mst_guest_detail);
          
          setFormData(prev => ({
            ...prev,
            firstName: parsedUser.name ? parsedUser.name.split(' ')[0] : prev.firstName,
            lastName: parsedUser.name ? parsedUser.name.split(' ').slice(1).join(' ') : prev.lastName,
            email: parsedUser.email || prev.email,
            phone: parsedUser.phone || prev.phone
          }));
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      } else {
        setIsLoggedIn(false);
        setUserId(null);
        setIdMstGuestDetailId(null);
      }
    };

    checkUserLogin();
    
    const handleStorageChange = () => {
      checkUserLogin();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleInputChange = (field, value, extraData = null) => {
    if (field === 'title') {
      const selectedTitle = titles.find(t => t.name === value);
      setFormData(prev => ({ 
        ...prev, 
        title: value,
        title_id: selectedTitle ? selectedTitle.id.toString() : ''
      }));
    } else if (field === 'country') {
      const selectedCountry = countries.find(c => c.name === value);
      setFormData(prev => ({ 
        ...prev, 
        country: value,
        country_code: selectedCountry ? selectedCountry.code : 'IN'
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Prepare promo code data for API
  const preparePromoData = (code) => {
    const roomDetails = state.cart.map((item, index) => ({
      room_number: index + 1,
      category_id: item.categoryId,
      category_name: item.roomName,
      rate_plan_id: item.ratePlanId,
      rate_plan_name: item.rateName,
      adults_count: item.adultsCount || item.guestInfo?.adults || 1,
      children_count: item.childrenCount || item.guestInfo?.children || 0,
      room_price: item.roomPrice,
      offer_applied: item.offerName || '',
      quantity: item.quantity
    }));

    const totalGuests = roomDetails.reduce((total, room) => {
      return total + (room.adults_count + room.children_count) * room.quantity;
    }, 0);

    const promoData = {
      promo_code: code,
      booking_details: {
        hotel_id: state.searchState.hotelId,
        hotel_name: state.searchState.hotel,
        check_in: state.searchState.checkin,
        check_out: state.searchState.checkout,
        total_nights: nights,
        total_guests: totalGuests,
        total_rooms: state.cart.reduce((total, item) => total + item.quantity, 0)
      },
      room_details: roomDetails,
      subtotal: state.subtotal
    };

    return promoData;
  };

  // Apply promo code handler - FIXED VERSION
// const handleApplyPromo = async () => {
//   if (!promoCode.trim()) {
//     setPromoError('Please enter a promo code.');
//     return;
//   }

//   if (!canApplyPromo()) {
//     if (isWidgetPromo) {
//       setPromoError("Widget promo code is already applied.");
//     } else if (hasAnyNonPromoOfferApplied()) {
//       setPromoError("Promo code cannot be applied as rooms already have other offers/discounts.");
//     } else if (hasWidgetPromoOffer()) {
//       setPromoError("Promo code already applied from widget.");
//     } else {
//       setPromoError("Promo code already applied.");
//     }
//     setPromoCode('');
//     return;
//   }

//   if (state.hasDiscount) {
//     setPromoError("Discount already applied.");
//     return;
//   }

//   setLoading(true);
//   setPromoError('');
//   setPromoSuccess(false);
//   setIsApplyingPromo(true);
  
//   try {
//     const promoData = preparePromoData(promoCode);
//     const response = await apiService.applyPromoCode(promoData);
    
//     if (response.success) {
//       // Get discount information from the response
//       const discountType = response.data?.promo_code_info?.discount_type;
//       const discountValue = response.data?.promo_code_info?.discount_value;
      
//       // Calculate discount amount properly
//       let discountAmount = 0;
//       let discountedSubtotal = 0;
//       let discountedTaxes = 0;
//       let discountedTotal = 0;
      
//       // For percentage discounts - get from savings_summary
//       if (discountType === 'percentage') {
//         discountAmount = response.data?.savings_summary?.total_savings || 0;
//         discountedSubtotal = response.data?.discounted_charges?.subtotal || 0;
//         discountedTaxes = response.data?.discounted_charges?.taxes?.total_tax || 0;
//         discountedTotal = response.data?.discounted_charges?.total || 0;
//       } 
//       // For fixed discounts - calculate properly
//       else if (discountType === 'fixed') {
//         // Fixed discount should be applied per booking, not per room
//         // The API might not calculate it correctly, so we calculate it ourselves
        
//         // For fixed discount, the discount amount should be the fixed value (e.g., 100)
//         discountAmount = discountValue || 0;
        
//         // Calculate discounted subtotal (original subtotal - discount)
//         discountedSubtotal = Math.max(0, state.subtotal - discountAmount);
        
//         // Calculate taxes on the discounted amount (using same tax rate)
//         // Assuming 18% tax rate (you might want to get this from the response)
//         const taxRate = 0.18; // 18%
//         discountedTaxes = discountedSubtotal * taxRate;
        
//         // Total is subtotal + taxes
//         discountedTotal = discountedSubtotal + discountedTaxes;
//       }
      
//       // Update cart items with discounted prices from API response
//       const updatedCart = state.cart.map((item, index) => {
//         const roomDetail = response.data?.room_details?.find(r => r.room_number === index + 1);
        
//         // For fixed discount, we need to distribute the discount proportionally
//         if (discountType === 'fixed' && roomDetail) {
//           // Use the discounted price from API if available, otherwise calculate
//           const discountedPricePerNight = roomDetail?.discounted_price_per_night || 
//             (item.roomPrice - (discountAmount / (state.cart.length * nights)));
          
//           return {
//             ...item,
//             discountedPricePerNight: Math.max(0, discountedPricePerNight),
//             originalPrice: roomDetail?.original_price_per_night || item.roomPrice,
//             discountAmount: roomDetail?.discount_amount || (item.roomPrice - discountedPricePerNight)
//           };
//         } 
//         // For percentage discount
//         else if (roomDetail) {
//           return {
//             ...item,
//             discountedPricePerNight: roomDetail?.discounted_price_per_night || item.roomPrice * 0.9,
//             originalPrice: roomDetail?.original_price_per_night || item.roomPrice,
//             discountAmount: roomDetail?.discount_amount || 0
//           };
//         }
        
//         return item;
//       });
      
//       dispatch({ 
//         type: 'APPLY_MANUAL_PROMO', 
//         payload: { 
//           discountAmount,
//           promoCode: promoCode.toUpperCase(),
//           discountedSubtotal,
//           discountedTaxes,
//           discountedTotal,
//           finalTotal: discountedTotal,
//           promoCodeInfo: {
//             ...response.data?.promo_code_info,
//             id: response.data?.room_details?.[0]?.offer?.id,
//             room_details: response.data?.room_details,
//             discounted_charges: response.data?.discounted_charges,
//             tax_summary: response.data?.discounted_charges?.tax_summary
//           },
//           updatedCart,
//           fromWidget: false
//         }  
//       });
      
//       setPromoSuccess(true);
//       setPromoError('');
      
//       // Show success message based on discount type
//       if (discountType === 'fixed') {
//         alert(`Success: ${formatCurrency(discountAmount)} Fixed Discount Applied!`);
//       } else {
//         alert(`Success: ${discountValue}% Discount Applied!`);
//       }
//     } else {
//       setPromoError(response.message || 'Invalid promo code. Please try again.');
//       setPromoSuccess(false);
//       setPromoCode('');
//     }
//   } catch (error) {
//     console.error('Failed to apply promo code:', error);
    
//     // Fallback to mock validation
//     const mockResult = await validatePromoCodeMock(promoCode, state.cart, state.subtotal);
//     if (mockResult.success) {
//       dispatch({ 
//         type: 'APPLY_DISCOUNT', 
//         payload: { 
//           discountAmount: mockResult.discountAmount,
//           promoCode: promoCode.toUpperCase(),
//           fromWidget: false
//         } 
//       });
//       setPromoSuccess(true);
//       setPromoError('');
//       alert(`Success: ${formatCurrency(mockResult.discountAmount)} Discount Applied!`);
//     } else {
//       setPromoError(mockResult.message);
//       setPromoSuccess(false);
//       setPromoCode('');
//     }
//   } finally {
//     setLoading(false);
//     setIsApplyingPromo(false);
//   }
// };

// Apply promo code handler - FIXED VERSION
const handleApplyPromo = async () => {
  if (!promoCode.trim()) {
    setPromoError('Please enter a promo code.');
    return;
  }

  if (!canApplyPromo()) {
    if (isWidgetPromo) {
      setPromoError("Widget promo code is already applied.");
    } else if (hasAnyNonPromoOfferApplied()) {
      setPromoError("Promo code cannot be applied as rooms already have other offers/discounts.");
    } else if (hasWidgetPromoOffer()) {
      setPromoError("Promo code already applied from widget.");
    } else {
      setPromoError("Promo code already applied.");
    }
    setPromoCode('');
    return;
  }

  if (state.hasDiscount) {
    setPromoError("Discount already applied.");
    return;
  }

  setLoading(true);
  setPromoError('');
  setPromoSuccess(false);
  setIsApplyingPromo(true);
  
  try {
    const promoData = preparePromoData(promoCode);
    const response = await apiService.applyPromoCode(promoData);
    
    if (response.success) {
      // Get all the data from the response
      const responseData = response.data;
      const discountType = responseData?.promo_code_info?.discount_type;
      const discountValue = responseData?.promo_code_info?.discount_value;
      
      // USE THE API'S CALCULATED VALUES DIRECTLY - DON'T RECALCULATE
      const discountedSubtotal = responseData?.discounted_charges?.subtotal || 0;
      const discountedTaxes = responseData?.discounted_charges?.taxes?.total_tax || 0;
      const discountedTotal = responseData?.discounted_charges?.total || 0;
      const discountAmount = responseData?.savings_summary?.total_savings || 0;
      const roomDetailsFromResponse = responseData?.room_details || [];
      
      // Update cart items with discounted prices from API response
      const updatedCart = state.cart.map((item, index) => {
        const roomDetail = roomDetailsFromResponse.find(r => r.room_number === index + 1);
        
        if (roomDetail) {
          // Use the discounted price per night from API response
          const discountedPricePerNight = roomDetail.discounted_price_per_night;
          const discountAmountPerRoom = roomDetail.discount_amount || 0;
          
          return {
            ...item,
            discountedPricePerNight: discountedPricePerNight,
            originalPrice: roomDetail.original_price_per_night || item.roomPrice,
            discountAmount: discountAmountPerRoom,
            roomPrice: item.roomPrice // Keep original price for reference
          };
        }
        
        return item;
      });
      
      // Dispatch with the API's calculated values
      dispatch({ 
        type: 'APPLY_MANUAL_PROMO', 
        payload: { 
          discountAmount, // This should be 300 from savings_summary
          promoCode: promoCode.toUpperCase(),
          discountedSubtotal, // This should be 29700 from discounted_charges
          discountedTaxes, // This should be 5346 from discounted_charges.taxes
          discountedTotal, // This should be 35046 from discounted_charges.total
          finalTotal: discountedTotal,
          promoCodeInfo: {
            ...responseData?.promo_code_info,
            id: responseData?.room_details?.[0]?.offer?.id,
            room_details: responseData?.room_details,
            discounted_charges: responseData?.discounted_charges,
            savings_summary: responseData?.savings_summary,
            tax_summary: responseData?.discounted_charges?.tax_summary
          },
          updatedCart,
          fromWidget: false
        }  
      });
      
      setPromoSuccess(true);
      setPromoError('');
      
      // Show success message
      alert(`Success: ₹${discountAmount} Discount Applied! (₹100 per night for 3 nights)`);
    } else {
      setPromoError(response.message || 'Invalid promo code. Please try again.');
      setPromoSuccess(false);
      setPromoCode('');
    }
  } catch (error) {
    console.error('Failed to apply promo code:', error);
    setPromoError('Failed to apply promo code. Please try again.');
    setPromoSuccess(false);
  } finally {
    setLoading(false);
    setIsApplyingPromo(false);
  }
};
  // Mock API call for promo code validation (fallback)
  const validatePromoCodeMock = async (code, cartItems, subtotal) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (code.toUpperCase() === 'PROMO2025') {
          resolve({
            success: true,
            discountAmount: 4160,
            discountType: 'fixed',
            message: 'Promo code applied successfully!',
            newTotal: subtotal - 4160
          });
        } else if (code.toUpperCase() === 'SAVE10') {
          const discount = subtotal * 0.1;
          resolve({
            success: true,
            discountAmount: Math.round(discount),
            discountType: 'percentage',
            message: '10% discount applied successfully!',
            newTotal: subtotal - discount
          });
        } else {
          resolve({
            success: false,
            message: 'Invalid promo code. Please try again.'
          });
        }
      }, 1000);
    });
  };

   // Add this useEffect to clean up discount when navigating away
    useEffect(() => {
      return () => {
        // Clean up discount when component unmounts (navigating away)
        if (state.hasDiscount) {
          dispatch({ type: 'CLEAR_DISCOUNT' });
        }
      };
    }, [dispatch, state.hasDiscount]);

    // // Add this to detect when cart changes (user modified selection)
    // useEffect(() => {
    //   // If cart changes and we have a discount from manual promo (not widget),
    //   // we should clear it because the pricing basis has changed
    //   if (state.hasDiscount && !state.fromWidget && state.promoCode) {
    //     // You might want to show a message to user
    //     console.log('Cart changed, clearing discount');
    //     dispatch({ type: 'CLEAR_DISCOUNT' });
    //     setPromoSuccess(false);
    //     setPromoCode('');
    //   }
    // }, [state.cart]); // Only depend on cart changes
    

    
  // Prepare booking data
  // const prepareBookingData = () => {
  //   const shopCode = process.env.REACT_APP_SHOP_CODE || 'whbe';
  //   const shopId = process.env.REACT_APP_SHOP_ID || '1';

  //   const subtotal = state.subtotal;
  //   const taxes = state.taxes;
  //   const discountAmount = state.hasDiscount ? state.discountAmount : 0;
  //   const promoCodeUsed = state.hasDiscount ? state.promoCode : '';
  //   const finalTotal = state.hasDiscount ? state.finalTotal : state.total;
  //   const nights = calculateNights();

  //   const roomDetails = state.cart.map((item, index) => {
  //     let roomPricePerNight = item.roomPrice;
  //     let totalRoomPrice = 0;
  //     let totalTaxForStay = 0;
  //     let originalTotalPrice = 0;
  //     let taxPercentage = 18;
      
  //     if (item.isFreeNightOffer && item.nights >= (item.ratePlan?.offer?.min_stay_nights || 1)) {
  //       totalRoomPrice = item.totalForStay || 0;
  //       totalTaxForStay = item.taxData ? 
  //         (item.taxData.total_cgst_amount_after_tax || 0) + 
  //         (item.taxData.total_sgst_amount_after_tax || 0) + 
  //         (item.taxData.total_cess_amount || 0) : 0;
        
  //       roomPricePerNight = item.nights > 0 ? totalRoomPrice / item.nights : item.roomPrice;
        
  //       if (item.taxData && item.taxData.base_amount_total) {
  //         taxPercentage = (totalTaxForStay / item.taxData.base_amount_total) * 100;
  //       }
        
  //       originalTotalPrice = item.originalTotalForStay || (item.originalPrice * item.nights);
  //     } else {
  //       totalRoomPrice = item.roomPrice * (item.nights || nights);
  //       totalTaxForStay = item.taxData ? 
  //         (item.taxData.total_cgst_amount_after_tax || 0) + 
  //         (item.taxData.total_sgst_amount_after_tax || 0) + 
  //         (item.taxData.total_cess_amount || 0) : (totalRoomPrice * 0.18);
        
  //       if (item.taxData && item.taxData.base_amount_total) {
  //         taxPercentage = (totalTaxForStay / item.taxData.base_amount_total) * 100;
  //       }
        
  //       originalTotalPrice = item.originalPrice ? item.originalPrice * (item.nights || nights) : totalRoomPrice * 1.5;
  //     }

  //     const taxPerNight = item.nights > 0 ? totalTaxForStay / (item.nights || nights) : 0;

  //     let originalPricePerNight = item.originalPrice;
      
  //     if (!originalPricePerNight && item.discountInfo) {
  //       if (item.discountInfo.type === 'percentage') {
  //         const discountDecimal = item.discountInfo.rawValue / 100;
  //         originalPricePerNight = item.roomPrice / (1 - discountDecimal);
  //       } else if (item.discountInfo.type === 'fixed') {
  //         originalPricePerNight = item.roomPrice + item.discountInfo.rawValue;
  //       }
  //     }
      
  //     const discountId = item.discountId || 
  //                       (item.ratePlan?.offer?.id) || 
  //                       (item.discountInfo?.discountId) || 
  //                       null;

  //     const offerApplied = item.offerName || 
  //                         (item.ratePlan?.offer?.discount_name) || 
  //                         (item.discountInfo?.name) || 
  //                         '';

  //     let adultsCount = 2;
  //     let childrenCount = 0;
      
  //     if (item.guestInfo) {
  //       adultsCount = item.guestInfo.adults || item.adultsCount || 2;
  //       childrenCount = item.guestInfo.children || item.childrenCount || 0;
  //     } else if (item.originalGuestConfig) {
  //       adultsCount = item.originalGuestConfig.adults || 2;
  //       childrenCount = item.originalGuestConfig.children || 0;
  //     } else if (state.searchState?.room_guest && item.roomNumber) {
  //       const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
  //       if (roomGuest) {
  //         adultsCount = roomGuest.no_of_adults || 2;
  //         childrenCount = roomGuest.no_of_children || 0;
  //       } else {
  //         adultsCount = item.adultsCount || 2;
  //         childrenCount = item.childrenCount || 0;
  //       }
  //     } else {
  //       adultsCount = item.adultsCount || 2;
  //       childrenCount = item.childrenCount || 0;
  //     }
      
  //     return {
  //       room_number: index + 1,
  //       category_name: item.roomName,
  //       rate_plan_name: item.rateName,
  //       adults_count: adultsCount,
  //       children_count: childrenCount,
  //       room_price: Math.round(roomPricePerNight),
  //       total_room_price: Math.round(totalRoomPrice),
  //       offer_applied: offerApplied,
  //       quantity: item.quantity || 1,
  //       discount_id: discountId,
  //       original_price: Math.round(originalPricePerNight),
  //       total_original_price: Math.round(originalTotalPrice),
  //       tax_per_room: Math.round(totalTaxForStay),
  //       tax_per_night: Math.round(taxPerNight),
  //       tax_percentage: parseFloat(taxPercentage.toFixed(2)),
  //       is_free_night_offer: item.isFreeNightOffer || false,
  //       free_nights: item.freeNights || 0,
  //       payable_nights: item.payableNights || item.nights || nights,
  //       total_nights: item.nights || nights,
  //       tax_breakdown: item.taxData ? {
  //         base_amount: Math.round(item.taxData.base_amount_total || 0),
  //         cgst: Math.round(item.taxData.total_cgst_amount_after_tax || 0),
  //         sgst: Math.round(item.taxData.total_sgst_amount_after_tax || 0),
  //         cess: Math.round(item.taxData.total_cess_amount || 0)
  //       } : null
  //     };
  //   });

  //   const totalGuests = roomDetails.reduce((total, room) => {
  //     return total + (room.adults_count + room.children_count) * room.quantity;
  //   }, 0);

  //   const overallTaxPercentage = subtotal > 0 ? (taxes / subtotal) * 100 : 18;

  //   const bookingData = {
  //     shop_code: shopCode,
  //     shop_id: shopId,
  //     guest_info: {
  //       title: formData.title,
  //       title_id: formData.title_id || '1',
  //       first_name: formData.firstName,
  //       last_name: formData.lastName,
  //       date_of_birth: formData.dateOfBirth || null,
  //       gst_number: formData.gstNumber || '',
  //       email: formData.email,
  //       phone: formData.phone,
  //       country: formData.country,
  //       country_code: formData.country_code || 'IN',
  //       city: formData.city,
  //       state: formData.state,
  //       pincode: formData.pincode,
  //       special_requests: formData.specialRequests || '',
  //       user_id: userId || null,
  //       id_mst_guest_detail: idMstGuestDetailId || null
  //     },
  //     booking_details: {
  //       hotel_id: state.searchState.hotelId,
  //       hotel_name: state.searchState.hotel,
  //       check_in: state.searchState.checkin,
  //       check_out: state.searchState.checkout,
  //       total_nights: nights,
  //       total_guests: totalGuests,
  //       total_rooms: state.cart.reduce((total, item) => total + (item.quantity || 1), 0)
  //     },
  //     room_details: roomDetails,
  //     source: 'booking_engine',
  //     pricing: {
  //       subtotal: Math.round(subtotal),
  //       taxes: Math.round(taxes),
  //       tax_percentage: parseFloat(overallTaxPercentage.toFixed(2)),
  //       discount_amount: Math.round(discountAmount),
  //       promo_code: promoCodeUsed,
  //       final_total: Math.round(finalTotal),
  //       currency: 'INR',
  //       tax_breakdown: state.taxBreakdown ? {
  //         cgst: Math.round(state.taxBreakdown.cgst || 0),
  //         sgst: Math.round(state.taxBreakdown.sgst || 0),
  //         cess: Math.round(state.taxBreakdown.cess || 0)
  //       } : null
  //     },
  //     metadata: {
  //       booking_timestamp: new Date().toISOString()
  //     }
  //   };

  //   return bookingData;
  // };
  // Prepare booking data - FIXED for manual promo codes for chile age
// const prepareBookingData = () => {
//   const shopCode = process.env.REACT_APP_SHOP_CODE || 'whbe';
//   const shopId = process.env.REACT_APP_SHOP_ID || '1';
//   const nights = calculateNights();

//   // Determine if we have a manually applied promo code
//   const hasManualPromo = state.hasDiscount && !state.fromWidget && state.discountedSubtotal;
  
//   // Use discounted values if manual promo is applied, otherwise use regular state values
//   const subtotal = hasManualPromo ? state.discountedSubtotal : state.subtotal;
//   const taxes = hasManualPromo ? state.discountedTaxes : state.taxes;
//   const discountAmount = hasManualPromo ? state.discountAmount : 0;
//   const promoCodeUsed = hasManualPromo ? state.promoCode : '';
//   const finalTotal = hasManualPromo ? (state.discountedSubtotal + state.discountedTaxes) : state.total;

//   // Get tax rate from promo response if available
//   let taxRate = 18; // Default fallback
//   if (hasManualPromo && state.promoCodeInfo?.discounted_charges?.tax_summary?.tax_rate) {
//     taxRate = state.promoCodeInfo.discounted_charges.tax_summary.tax_rate;
//   } else if (hasManualPromo && state.promoCodeInfo?.tax_summary?.tax_rate) {
//     taxRate = state.promoCodeInfo.tax_summary.tax_rate;
//   }

//   const roomDetails = state.cart.map((item, index) => {
//     let roomPricePerNight = item.roomPrice;
//     let totalRoomPrice = 0;
//     let totalTaxForStay = 0;
//     let originalTotalPrice = 0;
//     let taxPercentage = taxRate; // Use the tax rate from promo response
    
//     // For manually applied promo, use discounted price per night from state
//     if (hasManualPromo && item.discountedPricePerNight) {
//       roomPricePerNight = item.discountedPricePerNight;
//       totalRoomPrice = roomPricePerNight * item.quantity * nights;
      
//       // Get tax amount from promo response if available
//       if (state.promoCodeInfo?.discounted_charges?.taxes?.total_tax) {
//         totalTaxForStay = state.promoCodeInfo.discounted_charges.taxes.total_tax / state.cart.length;
//       } else if (state.promoCodeInfo?.room_details?.[index]?.tax_details?.total_tax) {
//         totalTaxForStay = state.promoCodeInfo.room_details[index].tax_details.total_tax;
//       } else {
//         totalTaxForStay = (totalRoomPrice * taxRate) / 100;
//       }
      
//       originalTotalPrice = item.originalPrice * item.quantity * nights;
//     }
//     // For free night offers
//     else if (item.isFreeNightOffer && item.nights >= (item.ratePlan?.offer?.min_stay_nights || 1)) {
//       totalRoomPrice = item.totalForStay || 0;
//       totalTaxForStay = item.taxData ? 
//         (item.taxData.total_cgst_amount_after_tax || 0) + 
//         (item.taxData.total_sgst_amount_after_tax || 0) + 
//         (item.taxData.total_cess_amount || 0) : 0;
      
//       roomPricePerNight = item.nights > 0 ? totalRoomPrice / item.nights : item.roomPrice;
      
//       if (item.taxData && item.taxData.base_amount_total) {
//         taxPercentage = (totalTaxForStay / item.taxData.base_amount_total) * 100;
//       }
      
//       originalTotalPrice = item.originalTotalForStay || (item.originalPrice * item.nights);
//     } 
//     // Regular pricing
//     else {
//       totalRoomPrice = item.roomPrice * (item.nights || nights) * item.quantity;
//       totalTaxForStay = item.taxData ? 
//         (item.taxData.total_cgst_amount_after_tax || 0) + 
//         (item.taxData.total_sgst_amount_after_tax || 0) + 
//         (item.taxData.total_cess_amount || 0) : (totalRoomPrice * taxRate / 100);
      
//       if (item.taxData && item.taxData.base_amount_total) {
//         taxPercentage = (totalTaxForStay / item.taxData.base_amount_total) * 100;
//       } else {
//         taxPercentage = taxRate;
//       }
      
//       originalTotalPrice = item.originalPrice ? item.originalPrice * (item.nights || nights) * item.quantity : totalRoomPrice * 1.5;
//     }

//     const taxPerNight = (item.nights || nights) > 0 ? totalTaxForStay / (item.nights || nights) : 0;

//     let originalPricePerNight = item.originalPrice;
    
//     if (!originalPricePerNight && item.discountInfo) {
//       if (item.discountInfo.type === 'percentage') {
//         const discountDecimal = item.discountInfo.rawValue / 100;
//         originalPricePerNight = item.roomPrice / (1 - discountDecimal);
//       } else if (item.discountInfo.type === 'fixed') {
//         originalPricePerNight = item.roomPrice + item.discountInfo.rawValue;
//       }
//     }
    
//     // Get discount ID from promo code info or room details
//     let discountId = null;
    
//     if (hasManualPromo) {
//       // First try to get from promoCodeInfo if available
//       if (state.promoCodeInfo?.id) {
//         discountId = state.promoCodeInfo.id;
//       } 
//       // Then try to get from the item's offer if available (from the API response)
//       else if (item.offer?.id) {
//         discountId = item.offer.id;
//       }
//       // Check if we have any room_details in state that might contain the offer ID
//       else if (state.promoCodeInfo?.room_details?.[index]?.offer?.id) {
//         discountId = state.promoCodeInfo.room_details[index].offer.id;
//       }
//     }
    
//     // Fallback to existing discount sources if no promo discount ID found
//     if (!discountId) {
//       discountId = item.discountId || 
//                   (item.ratePlan?.offer?.id) || 
//                   (item.discountInfo?.discountId) || 
//                   null;
//     }

//     const offerApplied = hasManualPromo 
//       ? `Promo Code: ${state.promoCode}`
//       : (item.offerName || item.ratePlan?.offer?.discount_name || item.discountInfo?.name || '');

//     let adultsCount = 2;
//     let childrenCount = 0;
    
//     if (item.guestInfo) {
//       adultsCount = item.guestInfo.adults || item.adultsCount || 2;
//       childrenCount = item.guestInfo.children || item.childrenCount || 0;
//     } else if (item.originalGuestConfig) {
//       adultsCount = item.originalGuestConfig.adults || 2;
//       childrenCount = item.originalGuestConfig.children || 0;
//     } else if (state.searchState?.room_guest && item.roomNumber) {
//       const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
//       if (roomGuest) {
//         adultsCount = roomGuest.no_of_adults || 2;
//         childrenCount = roomGuest.no_of_children || 0;
//       } else {
//         adultsCount = item.adultsCount || 2;
//         childrenCount = item.childrenCount || 0;
//       }
//     } else {
//       adultsCount = item.adultsCount || 2;
//       childrenCount = item.childrenCount || 0;
//     }
    
//     // For manually applied promo, use the discounted price from the API response
//     const roomPriceToSend = hasManualPromo && item.discountedPricePerNight 
//       ? Math.round(item.discountedPricePerNight) 
//       : Math.round(roomPricePerNight);
    
//     const totalRoomPriceToSend = hasManualPromo && item.discountedPricePerNight
//       ? Math.round(item.discountedPricePerNight * item.quantity * nights)
//       : Math.round(totalRoomPrice);
    
//     return {
//       room_number: index + 1,
//       category_name: item.roomName,
//       rate_plan_name: item.rateName,
//       adults_count: adultsCount,
//       children_count: childrenCount,
//       room_price: roomPriceToSend,
//       total_room_price: totalRoomPriceToSend,
//       offer_applied: offerApplied,
//       quantity: item.quantity || 1,
//       discount_id: discountId,
//       original_price: Math.round(originalPricePerNight || item.roomPrice),
//       total_original_price: Math.round(originalTotalPrice || (item.roomPrice * item.quantity * nights)),
//       tax_per_room: Math.round(hasManualPromo ? (state.discountedTaxes / state.cart.length) : totalTaxForStay),
//       tax_per_night: Math.round(hasManualPromo ? (state.discountedTaxes / state.cart.length / nights) : taxPerNight),
//       tax_percentage: parseFloat(taxPercentage.toFixed(2)), // Now uses actual tax rate from response
//       is_free_night_offer: item.isFreeNightOffer || false,
//       free_nights: item.freeNights || 0,
//       payable_nights: item.payableNights || item.nights || nights,
//       total_nights: item.nights || nights,
//       tax_breakdown: hasManualPromo ? {
//         base_amount: Math.round(subtotal / state.cart.length),
//         cgst: Math.round((state.discountedTaxes / 2) / state.cart.length),
//         sgst: Math.round((state.discountedTaxes / 2) / state.cart.length),
//         cess: 0
//       } : (item.taxData ? {
//         base_amount: Math.round(item.taxData.base_amount_total || 0),
//         cgst: Math.round(item.taxData.total_cgst_amount_after_tax || 0),
//         sgst: Math.round(item.taxData.total_sgst_amount_after_tax || 0),
//         cess: Math.round(item.taxData.total_cess_amount || 0)
//       } : null)
//     };
//   });

//   const totalGuests = roomDetails.reduce((total, room) => {
//     return total + (room.adults_count + room.children_count) * room.quantity;
//   }, 0);

//   const overallTaxPercentage = subtotal > 0 ? (taxes / subtotal) * 100 : taxRate;

//   const bookingData = {
//     shop_code: shopCode,
//     shop_id: shopId,
//     guest_info: {
//       title: formData.title,
//       title_id: formData.title_id || '1',
//       first_name: formData.firstName,
//       last_name: formData.lastName,
//       date_of_birth: formData.dateOfBirth || null,
//       gst_number: formData.gstNumber || '',
//       email: formData.email,
//       phone: formData.phone,
//       country: formData.country,
//       country_code: formData.country_code || 'IN',
//       city: formData.city,
//       state: formData.state,
//       pincode: formData.pincode,
//       special_requests: formData.specialRequests || '',
//       user_id: userId || null,
//       id_mst_guest_detail: idMstGuestDetailId || null
//     },
//     booking_details: {
//       hotel_id: state.searchState.hotelId,
//       hotel_name: state.searchState.hotel,
//       check_in: state.searchState.checkin,
//       check_out: state.searchState.checkout,
//       total_nights: nights,
//       total_guests: totalGuests,
//       total_rooms: state.cart.reduce((total, item) => total + (item.quantity || 1), 0)
//     },
//     room_details: roomDetails,
//     source: 'booking_engine',
//     pricing: {
//       subtotal: Math.round(subtotal),
//       taxes: Math.round(taxes),
//       tax_percentage: parseFloat(overallTaxPercentage.toFixed(2)), // Now uses actual tax rate
//       discount_amount: Math.round(discountAmount),
//       promo_code: promoCodeUsed,
//       final_total: Math.round(finalTotal),
//       currency: 'INR',
//       tax_breakdown: hasManualPromo ? {
//         cgst: Math.round(taxes / 2),
//         sgst: Math.round(taxes / 2),
//         cess: 0
//       } : (state.taxBreakdown ? {
//         cgst: Math.round(state.taxBreakdown.cgst || 0),
//         sgst: Math.round(state.taxBreakdown.sgst || 0),
//         cess: Math.round(state.taxBreakdown.cess || 0)
//       } : null)
//     },
//     metadata: {
//       booking_timestamp: new Date().toISOString()
//     }
//   };

//   return bookingData;
// };


const prepareBookingData = () => {
  const shopCode = process.env.REACT_APP_SHOP_CODE || 'whbe';
  const shopId = process.env.REACT_APP_SHOP_ID || '1';
  const nights = calculateNights();

  console.log('=== DEBUG: prepareBookingData START ===');
  console.log('Cart items:', JSON.stringify(state.cart.map(item => ({
    roomNumber: item.roomNumber,
    guestInfo: item.guestInfo,
    originalGuestConfig: item.originalGuestConfig
  })), null, 2));

  // Determine if we have a manually applied promo code
  const hasManualPromo = state.hasDiscount && !state.fromWidget && state.discountedSubtotal;
  
  // Use discounted values if manual promo is applied, otherwise use regular state values
  const subtotal = hasManualPromo ? state.discountedSubtotal : state.subtotal;
  const taxes = hasManualPromo ? state.discountedTaxes : state.taxes;
  const discountAmount = hasManualPromo ? state.discountAmount : 0;
  const promoCodeUsed = hasManualPromo ? state.promoCode : '';
  const finalTotal = hasManualPromo ? (state.discountedSubtotal + state.discountedTaxes) : state.total;

  // Get tax rate from promo response if available
  let taxRate = 18; // Default fallback
  if (hasManualPromo && state.promoCodeInfo?.discounted_charges?.tax_summary?.tax_rate) {
    taxRate = state.promoCodeInfo.discounted_charges.tax_summary.tax_rate;
  } else if (hasManualPromo && state.promoCodeInfo?.tax_summary?.tax_rate) {
    taxRate = state.promoCodeInfo.tax_summary.tax_rate;
  }

  const roomDetails = state.cart.map((item, index) => {
    let roomPricePerNight = item.roomPrice;
    let totalRoomPrice = 0;
    let totalTaxForStay = 0;
    let originalTotalPrice = 0;
    let taxPercentage = taxRate;
    
    // Get guest configuration including child ages - FIXED ORDER
    let adultsCount = 2;
    let childrenCount = 0;
    let childAges = []; // Array to store child ages
    
    console.log(`\n--- Processing Room ${index + 1} (Room Number: ${item.roomNumber}) ---`);
    
    // IMPORTANT: Check originalGuestConfig FIRST since that's where child ages are stored
    if (item.originalGuestConfig) {
      console.log('Method: Using item.originalGuestConfig (FIRST PRIORITY)');
      adultsCount = item.originalGuestConfig.adults || 2;
      childrenCount = item.originalGuestConfig.children || 0;
      childAges = item.originalGuestConfig.childAges || [];
      console.log('  originalGuestConfig:', item.originalGuestConfig);
      console.log('  childAges from originalGuestConfig:', childAges);
    } 
    // Method 2: Try to get from guestInfo (second priority)
    else if (item.guestInfo) {
      console.log('Method 2: Using item.guestInfo');
      adultsCount = item.guestInfo.adults || item.adultsCount || 2;
      childrenCount = item.guestInfo.children || item.childrenCount || 0;
      childAges = item.guestInfo.childAges || [];
      console.log('  guestInfo:', item.guestInfo);
      console.log('  childAges from guestInfo:', childAges);
    } 
    // Method 3: Get from searchState using roomNumber
    else if (state.searchState?.room_guest && item.roomNumber) {
      console.log('Method 3: Using searchState.room_guest');
      const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
      if (roomGuest) {
        adultsCount = roomGuest.no_of_adults || 2;
        childrenCount = roomGuest.no_of_children || 0;
        childAges = roomGuest.child_ages || [];
        console.log('  roomGuest:', roomGuest);
        console.log('  childAges from room_guest:', childAges);
      } else {
        adultsCount = item.adultsCount || 2;
        childrenCount = item.childrenCount || 0;
      }
    } 
    // Method 4: Fallback to item properties
    else {
      console.log('Method 4: Using item properties fallback');
      adultsCount = item.adultsCount || 2;
      childrenCount = item.childrenCount || 0;
      childAges = item.childAges || []; // Try direct property
      console.log('  childAges from item:', childAges);
    }
    
    console.log('Final values for this room:');
    console.log('  adultsCount:', adultsCount);
    console.log('  childrenCount:', childrenCount);
    console.log('  childAges (raw):', childAges);
    console.log('  childAges type:', typeof childAges, Array.isArray(childAges) ? 'array' : 'not array');
    
    // For manually applied promo, use discounted price per night from state
    if (hasManualPromo && item.discountedPricePerNight) {
      roomPricePerNight = item.discountedPricePerNight;
      totalRoomPrice = roomPricePerNight * item.quantity * nights;
      
      // Get tax amount from promo response if available
      if (state.promoCodeInfo?.discounted_charges?.taxes?.total_tax) {
        totalTaxForStay = state.promoCodeInfo.discounted_charges.taxes.total_tax / state.cart.length;
      } else if (state.promoCodeInfo?.room_details?.[index]?.tax_details?.total_tax) {
        totalTaxForStay = state.promoCodeInfo.room_details[index].tax_details.total_tax;
      } else {
        totalTaxForStay = (totalRoomPrice * taxRate) / 100;
      }
      
      originalTotalPrice = item.originalPrice * item.quantity * nights;
    }
    // For free night offers
    else if (item.isFreeNightOffer && item.nights >= (item.ratePlan?.offer?.min_stay_nights || 1)) {
      totalRoomPrice = item.totalForStay || 0;
      totalTaxForStay = item.taxData ? 
        (item.taxData.total_cgst_amount_after_tax || 0) + 
        (item.taxData.total_sgst_amount_after_tax || 0) + 
        (item.taxData.total_cess_amount || 0) : 0;
      
      roomPricePerNight = item.nights > 0 ? totalRoomPrice / item.nights : item.roomPrice;
      
      if (item.taxData && item.taxData.base_amount_total) {
        taxPercentage = (totalTaxForStay / item.taxData.base_amount_total) * 100;
      }
      
      originalTotalPrice = item.originalTotalForStay || (item.originalPrice * item.nights);
    } 
    // Regular pricing
    else {
      totalRoomPrice = item.roomPrice * (item.nights || nights) * item.quantity;
      totalTaxForStay = item.taxData ? 
        (item.taxData.total_cgst_amount_after_tax || 0) + 
        (item.taxData.total_sgst_amount_after_tax || 0) + 
        (item.taxData.total_cess_amount || 0) : (totalRoomPrice * taxRate / 100);
      
      if (item.taxData && item.taxData.base_amount_total) {
        taxPercentage = (totalTaxForStay / item.taxData.base_amount_total) * 100;
      } else {
        taxPercentage = taxRate;
      }
      
      originalTotalPrice = item.originalPrice ? item.originalPrice * (item.nights || nights) * item.quantity : totalRoomPrice * 1.5;
    }

    const taxPerNight = (item.nights || nights) > 0 ? totalTaxForStay / (item.nights || nights) : 0;

    let originalPricePerNight = item.originalPrice;
    
    if (!originalPricePerNight && item.discountInfo) {
      if (item.discountInfo.type === 'percentage') {
        const discountDecimal = item.discountInfo.rawValue / 100;
        originalPricePerNight = item.roomPrice / (1 - discountDecimal);
      } else if (item.discountInfo.type === 'fixed') {
        originalPricePerNight = item.roomPrice + item.discountInfo.rawValue;
      }
    }
    
    // Get discount ID from promo code info or room details
let discountId = null;
let offerApplied = ''; // DECLARE ONCE HERE

if (hasManualPromo) {
  if (state.promoCodeInfo?.id) {
    discountId = state.promoCodeInfo.id;
  } else if (item.offer?.id) {
    discountId = item.offer.id;
  } else if (state.promoCodeInfo?.room_details?.[index]?.offer?.id) {
    discountId = state.promoCodeInfo.room_details[index].offer.id;
  }
}

if (!discountId) {
  discountId = item.discountId || 
              (item.ratePlan?.offer?.id) || 
              (item.discountInfo?.discountId) || 
              null;
}

// IMPORTANT: Check if this room has a promo applied
const hasPromoApplied = item.hasPromoApplied || (state.hasDiscount && !state.fromWidget && state.promoCode);

// SET offerApplied based on promo status
if (hasPromoApplied && state.promoCode) {
  // Use promo code offer ID from state
  if (state.promoCodeInfo?.id) {
    discountId = state.promoCodeInfo.id;
    offerApplied = `Promo Code: ${state.promoCode}`;
  } else if (state.promoCodeInfo?.room_details?.[index]?.offer?.id) {
    discountId = state.promoCodeInfo.room_details[index].offer.id;
    offerApplied = `Promo Code: ${state.promoCode}`;
  } else {
    // If no promo offer ID available, use a placeholder
    discountId = null;
    offerApplied = `Promo Code: ${state.promoCode}`;
  }
} else {
  // No promo applied, use original offer
  discountId = item.discountId || 
              (item.ratePlan?.offer?.id) || 
              (item.discountInfo?.discountId) || 
              null;
  offerApplied = item.offerName || 
                (item.ratePlan?.offer?.discount_name) || 
                (item.discountInfo?.name) || 
                '';
}

  
    // const offerApplied = hasManualPromo 
    //   ? `Promo Code: ${state.promoCode}`
    //   : (item.offerName || item.ratePlan?.offer?.discount_name || item.discountInfo?.name || '');
    
    // For manually applied promo, use the discounted price from the API response
    const roomPriceToSend = hasManualPromo && item.discountedPricePerNight 
      ? Math.round(item.discountedPricePerNight) 
      : Math.round(roomPricePerNight);
    
    const totalRoomPriceToSend = hasManualPromo && item.discountedPricePerNight
      ? Math.round(item.discountedPricePerNight * item.quantity * nights)
      : Math.round(totalRoomPrice);
    
    // Format child ages properly
    let formattedChildAges = [];
    
    if (Array.isArray(childAges) && childAges.length > 0) {
      console.log('Formatting child ages:', childAges);
      formattedChildAges = childAges.map(age => {
        // Handle different formats
        if (typeof age === 'object' && age !== null) {
          // If it's already in {age: value} format
          return { age: parseInt(age.age, 10) };
        } else {
          // If it's a direct age value
          return { age: parseInt(age, 10) };
        }
      });
      console.log('Formatted child ages:', formattedChildAges);
    } else {
      console.log('No child ages to format, childAges is:', childAges);
    }
    
    const roomDetail = {
       room_number: index + 1,
    category_name: item.roomName,
    rate_plan_name: item.rateName,
    adults_count: adultsCount,
    children_count: childrenCount,
    child_ages: formattedChildAges,
    room_price: roomPriceToSend,
    total_room_price: totalRoomPriceToSend,
    offer_applied: offerApplied, // Use the calculated offerApplied
    quantity: item.quantity || 1,
    discount_id: discountId, // Use the calculated discountId
    original_price: Math.round(originalPricePerNight || item.roomPrice),
    total_original_price: Math.round(originalTotalPrice),
    tax_per_room: Math.round(totalTaxForStay),
    tax_per_night: Math.round(taxPerNight),
    tax_percentage: parseFloat(taxPercentage.toFixed(2)),
    is_free_night_offer: hasPromoApplied ? false : (item.isFreeNightOffer || false),
    free_nights: hasPromoApplied ? 0 : (item.freeNights || 0),
      is_stackable_applied: item.isStackableApplied || false,
    payable_nights: item.payableNights || item.nights || nights,
    total_nights: item.nights || nights,
    tax_breakdown: hasPromoApplied ? {
      base_amount: Math.round(subtotal / state.cart.length),
      cgst: Math.round((state.discountedTaxes / 2) / state.cart.length),
      sgst: Math.round((state.discountedTaxes / 2) / state.cart.length),
      cess: 0
    } : (item.taxData ? {
      base_amount: Math.round(item.taxData.base_amount_total || 0),
      cgst: Math.round(item.taxData.total_cgst_amount_after_tax || 0),
      sgst: Math.round(item.taxData.total_sgst_amount_after_tax || 0),
      cess: Math.round(item.taxData.total_cess_amount || 0)
    } : null)
    };
    
    console.log('Final roomDetail child_ages:', roomDetail.child_ages);
    return roomDetail;
  });

  const totalGuests = roomDetails.reduce((total, room) => {
    return total + (room.adults_count + room.children_count) * room.quantity;
  }, 0);

  const overallTaxPercentage = subtotal > 0 ? (taxes / subtotal) * 100 : taxRate;

  const bookingData = {
    shop_code: shopCode,
    shop_id: shopId,
    guest_info: {
      title: formData.title,
      title_id: formData.title_id || '1',
      first_name: formData.firstName,
      last_name: formData.lastName,
      date_of_birth: formData.dateOfBirth || null,
      gst_number: formData.gstNumber || '',
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      country_code: formData.country_code || 'IN',
      city: formData.city,
      state: formData.state,
      pincode: formData.pincode,
      special_requests: formData.specialRequests || '',
      user_id: userId || null,
      id_mst_guest_detail: idMstGuestDetailId || null
    },
    booking_details: {
      hotel_id: state.searchState.hotelId,
      hotel_name: state.searchState.hotel,
      check_in: state.searchState.checkin,
      check_out: state.searchState.checkout,
      total_nights: nights,
      total_guests: totalGuests,
      total_rooms: state.cart.reduce((total, item) => total + (item.quantity || 1), 0)
    },
    room_details: roomDetails,
    source: 'booking_engine',
    pricing: {
      subtotal: Math.round(subtotal),
      taxes: Math.round(taxes),
      tax_percentage: parseFloat(overallTaxPercentage.toFixed(2)),
      discount_amount: Math.round(discountAmount),
      promo_code: promoCodeUsed,
      final_total: Math.round(finalTotal),
      currency: 'INR',
      tax_breakdown: hasManualPromo ? {
        cgst: Math.round(taxes / 2),
        sgst: Math.round(taxes / 2),
        cess: 0
      } : (state.taxBreakdown ? {
        cgst: Math.round(state.taxBreakdown.cgst || 0),
        sgst: Math.round(state.taxBreakdown.sgst || 0),
        cess: Math.round(state.taxBreakdown.cess || 0)
      } : null)
    },
    metadata: {
      booking_timestamp: new Date().toISOString()
    }
  };

  console.log('=== DEBUG: prepareBookingData END ===');
  console.log('Final room_details child_ages:', roomDetails.map(r => ({
    room: r.room_number,
    child_ages: r.child_ages
  })));

  return bookingData;
};

   const handleSubmit = async (e) => {
    if(e) e.preventDefault();
    
    if (isSubmitting || !isFormValid) return;

    setIsSubmitting(true);

    try {
      const bookingData = prepareBookingData();
      
      console.log('Submitting booking data:', bookingData);

      const apiResult = await apiService.confirmBooking(bookingData);

      if (apiResult.success) {
        const bookingReference = apiResult.data?.booking_reference || apiResult.booking_reference || 'N/A';
        const reservationId = apiResult.data?.reservation_id || apiResult.reservation_id || 'N/A';
        
        const paymentRedirectUrl = apiResult.data?.payment_redirect_url || apiResult.payment_redirect_url;
        
       if (paymentRedirectUrl) {
          const bookingInfo = {
            bookingReference,
            reservationId,
            hotelName: state.searchState.hotel,
            checkin: state.searchState.checkin,
            checkout: state.searchState.checkout,
            guestName: `${formData.firstName} ${formData.lastName}`,
            guestEmail: formData.email,
            finalTotal: state.hasDiscount ? state.finalTotal : state.total,
            timestamp: new Date().toISOString(),
            paymentStatus: 'pending',
            // NEW: We are saving the items and promo code to sessionStorage
            promoCode: state.promoCode || '',
            ecommerceItems: state.cart.map(item => ({
              item_id: item.roomId,
              item_name: item.roomName,
              affiliation: state.searchState.hotel || 'WelcomHeritage', // Added Property Name
              item_brand: state.searchState.hotel || 'WelcomHeritage',  // Added Property Name
              item_category: item.rateName,
              item_category2: state.searchState.hotelId,                // Added Property ID
              price: item.roomPrice,
              quantity: item.quantity
            }))
          };
          sessionStorage.setItem('bookingInfo', JSON.stringify(bookingInfo));
          
          setRedirectUrl(paymentRedirectUrl);
          setIsRedirecting(true);

          // Note: We removed the dataLayer.push from here!
          
          setTimeout(() => {
            // Do NOT clear the cart here either, wait until payment is successful
            window.location.href = paymentRedirectUrl;
          }, 100);
          
          return; // Exit early
        } else {
          const bookingInfo = {
            bookingReference,
            reservationId,
            hotelName: state.searchState.hotel,
            checkin: state.searchState.checkin,
            checkout: state.searchState.checkout,
            guestName: `${formData.firstName} ${formData.lastName}`,
            guestEmail: formData.email,
            finalTotal: state.hasDiscount ? state.finalTotal : state.total,
            timestamp: new Date().toISOString(),
            paymentStatus: 'completed',
            // Ensure items are saved here too for non-gateway bookings
            promoCode: state.promoCode || '',
            ecommerceItems: state.cart.map(item => ({
              item_id: item.roomId,
              item_name: item.roomName,
              affiliation: state.searchState.hotel || 'WelcomHeritage', // Added Property Name
              item_brand: state.searchState.hotel || 'WelcomHeritage',  // Added Property Name
              item_category: item.rateName,
              item_category2: state.searchState.hotelId,                // Added Property ID
              price: item.roomPrice,
              quantity: item.quantity
            }))
          };
          sessionStorage.setItem('bookingInfo', JSON.stringify(bookingInfo));
          
          sessionStorage.removeItem('bookingSession');
          dispatch({ type: 'CLEAR_CART' });
          navigateTo('confirmation');
        }
      } else {
        alert(`Booking failed: ${apiResult.message || 'Unknown error occurred. Please try again.'}`);
      }

    } catch (error) {
      console.error('Booking submission error:', error);
      alert('Failed to process booking. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderPromoCodeSection = () => (
  <div className="bg-white shadow-md rounded-xl p-4 lg:p-6 border border-gray-100">
    <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
      <div className="flex-shrink-0 flex items-center mb-3 sm:mb-0">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-brand-accent mr-2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
        </svg>
        <span className="text-md font-semibold text-text-primary">
          {isWidgetPromo || hasWidgetPromoOffer() ? "Promo Code Applied" : "Got a Promo Code?"}
        </span>
      </div>
      <div className="flex-grow flex items-center gap-3">
        <input
          type="text"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          disabled={!canApplyPromo() || isApplyingPromo || promoSuccess || loading}
          className="w-full flex-grow px-3 h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/50 focus:border-brand-accent text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          placeholder={
            !canApplyPromo() ? 
              (isWidgetPromo || hasWidgetPromoOffer() ? "Promo applied from widget" : 
               hasAnyNonPromoOfferApplied() ? "Not available with other offers" : 
               "Promo already applied") : 
              "Enter promo code"
          }
          readOnly={isWidgetPromo || hasWidgetPromoOffer() || promoSuccess}
        />
        <button
          type="button"
          onClick={handleApplyPromo}
          disabled={!canApplyPromo() || isApplyingPromo || !promoCode.trim() || promoSuccess || loading}
          className="bg-brand-accent text-text-dark-on-accent px-6 h-10 rounded-lg font-semibold text-sm hover:bg-brand-accent-hover transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] relative"
        >
          {isApplyingPromo || loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>...</span>
            </span>
          ) : (
            (state.hasDiscount || isWidgetPromo || hasWidgetPromoOffer() || promoSuccess) ? 'Applied' : 'Apply'
          )}
        </button>
      </div>
    </div>
    
    {/* Loading overlay for the entire promo section */}
    {loading && (
      <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
        <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-lg">
          <svg className="animate-spin h-5 w-5 text-brand-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium text-text-primary">Applying promo code...</span>
        </div>
      </div>
    )}
    
    {promoError && (
      <p className="text-sm text-red-600 mt-3 font-medium flex items-center gap-1">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
        </svg>
        {promoError}
      </p>
    )}
    
    {(isWidgetPromo || hasWidgetPromoOffer()) && state.hasDiscount && (
      <p className="text-sm text-green-600 mt-3 font-medium flex items-center gap-1">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
        </svg>
        Promo code <strong>{state.promoCode || state.searchState.promo_code}</strong> applied! You saved {formatCurrency(state.discountAmount)}.
      </p>
    )}
    
    {state.hasDiscount && !isWidgetPromo && !hasWidgetPromoOffer() && promoSuccess && (
      <p className="text-sm text-green-600 mt-3 font-medium flex items-center gap-1">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
        </svg>
        Code <strong>{state.promoCode}</strong> applied! You saved {formatCurrency(state.discountAmount)}.
      </p>
    )}
    
    {hasAnyNonPromoOfferApplied() && !state.hasDiscount && !isWidgetPromo && !hasWidgetPromoOffer() && (
      <p className="text-sm text-amber-600 mt-3 font-medium flex items-center gap-1">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
        </svg>
        Promo code cannot be applied as rooms already have other offers/discounts.
      </p>
    )}
  </div>
);

   if (!state.cart || state.cart.length === 0) {
    // If we're in the middle of redirecting, show payment processing message
    if (isRedirecting) {
      return (
        <div className="container mx-auto py-16 px-6 lg:px-8">
          <div className="max-w-md mx-auto text-center bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="mb-6">
              <svg className="animate-spin h-12 w-12 text-brand-accent mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-3">Redirecting to Payment</h2>
            <p className="text-text-secondary mb-4">
              Please wait while we securely redirect you to the payment gateway...
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 max-w-xs mx-auto">
              <div className="bg-brand-accent h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Do not close this window or press the back button
            </p>
            <button
              onClick={() => {
                window.location.href = redirectUrl;
              }}
              className="mt-4 text-sm text-brand-accent hover:underline"
            >
              Click here if not redirected automatically
            </button>
          </div>
        </div>
      );
    }
    
    // If not redirecting and no cart, show the original message
    return (
      <div className="container mx-auto py-8 px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-primary mb-4">No Booking Found</h2>
          <p className="text-text-secondary mb-6">Please select rooms before proceeding to checkout.</p>
          <button
            onClick={() => navigateTo('index')}
            className="bg-brand-accent text-text-dark-on-accent px-6 py-2 rounded-md font-semibold hover:bg-brand-accent-hover transition-colors"
          >
            Back to Room Selection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 lg:py-8 px-4 lg:px-8 pb-32 lg:pb-8">
    
      <div className="flex flex-col lg:flex-row lg:gap-8">
        
        {/* Main Content */}
        <div className="lg:w-2/3 w-full space-y-4 lg:space-y-6">
          {/* Back Button */}
          <button
             onClick={() => {
                // Clear any applied discount before navigating back
                if (state.hasDiscount) {
                  dispatch({ type: 'CLEAR_DISCOUNT' });
                }
                navigateTo('index');
              }}
            className="flex items-center text-sm font-medium text-text-secondary hover:text-text-primary py-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
            Back to Selection
          </button>

          {/* Mobile Summary Toggle */}
          <div className="lg:hidden bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <button 
              onClick={() => setIsMobileSummaryOpen(!isMobileSummaryOpen)}
              className="w-full flex justify-between items-center p-4 bg-gray-50 text-left"
            >
               <div className="flex items-center gap-2">
                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-brand-accent">
                   <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                 </svg>
                 <span className="font-semibold text-text-primary text-sm">Booking Summary</span>
               </div>
               <div className="flex items-center gap-2">
                 <span className="font-bold text-text-dark-on-accent">
                   {formatCurrency(state.hasDiscount ? state.finalTotal : state.total)}
                 </span>
                 <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={2} 
                    stroke="currentColor" 
                    className={`w-4 h-4 text-gray-500 transition-transform ${isMobileSummaryOpen ? 'rotate-180' : ''}`}
                  >
                   <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                 </svg>
               </div>
            </button>
            {isMobileSummaryOpen && (
              <div className="p-4 border-t border-gray-100 bg-white">
                <BookingSummary />
              </div>
            )}
          </div>

          <h1 className="text-2xl lg:text-3xl font-bold text-text-primary tracking-tight">Guest Information</h1>

          {/* Promo Code Section */}
          {/* {renderPromoCodeSection()} */}

          {/* Booking Form */}
          <div className="bg-white shadow-md rounded-xl p-5 lg:p-6 border border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <label htmlFor="title" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Title *
                  </label>
                  <div className="relative">
                    <select
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      required
                      disabled={isLoadingTitles}
                      className="w-full px-3 h-11 bg-white border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">{isLoadingTitles ? 'Loading titles...' : 'Select Title'}</option>
                      {titles.map((title) => (
                        <option key={title.id} value={title.name}>
                          {title.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>
                  <input type="hidden" value={formData.title_id} />
                </div>
                
                <div className="hidden md:block"></div>
                
                <div>
                  <label htmlFor="first-name" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first-name"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Jane"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="last-name" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last-name"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Doe"
                    autoComplete="family-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="email" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    inputMode="email"
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="jane.doe@example.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    inputMode="tel"
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="+91 98765 43210"
                    autoComplete="tel"
                  />
                </div>

                <div>
                  <label htmlFor="dob" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dob"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Country *
                  </label>
                  <div className="relative">
                    <select
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      required
                      disabled={isLoadingCountries}
                      className="w-full px-3 h-11 bg-white border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      autoComplete="country-name"
                    >
                      <option value="">{isLoadingCountries ? 'Loading countries...' : 'Select Country'}</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>
                  <input type="hidden" value={formData.country_code} />
                </div>
                <div>
                  <label htmlFor="city" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    required
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Mumbai"
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    State
                  </label>
                  <input
                    type="text"
                    id="state"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Maharashtra"
                    autoComplete="address-level1"
                  />
                </div>
                <div>
                  <label htmlFor="pincode" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    Pincode
                  </label>
                  <input
                    type="text"
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    pattern="[0-9]{6}"
                    maxLength="6"
                    inputMode="numeric"
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="400001"
                    autoComplete="postal-code"
                  />
                </div>
                
                <div>
                  <label htmlFor="gstNumber" className="block text-xs font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
                    GST Number (Optional)
                  </label>
                  <input
                    type="text"
                    id="gstNumber"
                    value={formData.gstNumber}
                    onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                    className="w-full px-3 h-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                    placeholder="Enter GSTIN"
                    maxLength="15"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="special-requests" className="block text-sm font-semibold text-text-primary mb-1">
                  Special Requests
                </label>
                <p className="text-xs text-text-secondary mb-2">(Optional)</p>
                <textarea
                  id="special-requests"
                  rows="4"
                  value={formData.specialRequests}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent/20 focus:border-brand-accent text-sm transition-all"
                  placeholder="e.g., High floor, early check-in, cot needed..."
                />
              </div>

              <div className="space-y-5 pt-2">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="text-sm font-semibold text-text-primary mb-3">2. Terms and Condition</h3>
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        checked={formData.terms}
                        onChange={(e) => handleInputChange('terms', e.target.checked)}
                        required
                        className="h-5 w-5 text-brand-accent rounded border-gray-300 focus:ring-brand-accent cursor-pointer"
                      />
                    </div>
                    <label htmlFor="terms" className="ml-3 text-sm text-text-secondary cursor-pointer select-none">
                      Yes, I agree with WelcomHeritage <a href="https://www.welcomheritagehotels.in/privacy-policy" 
                        target="_blank" className="font-medium text-brand-accent hover:underline">privacy policy</a>
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                  <h3 className="text-sm font-semibold text-text-primary mb-3">3. Cancellation Policy</h3>
                  <div className="flex items-start mb-3">
                    <div className="flex items-center h-5">
                      <input
                        id="cancellation"
                        name="cancellation"
                        type="checkbox"
                        checked={formData.cancellation}
                        onChange={(e) => handleInputChange('cancellation', e.target.checked)}
                        required
                        className="h-5 w-5 text-brand-accent rounded border-gray-300 focus:ring-brand-accent cursor-pointer"
                      />
                    </div>
                    <label htmlFor="cancellation" className="ml-3 text-sm text-text-secondary cursor-pointer select-none">
                      Yes, I agree with WelcomHeritage <a href="https://www.welcomheritagehotels.in/cancellation-policy" 
                        target="_blank" className="font-medium text-brand-accent hover:underline">cancellation policy</a>
                    </label>
                  </div>
                  <p className="text-xs text-text-secondary leading-relaxed pl-8">
                    By clicking on the button, I acknowledge and agree that above information is accurate and that my reservation will be subject to the Hotel Booking Terms and Policies.
                  </p>
                </div>
              </div>

              {/* Desktop Submit Button */}
              <div className="hidden lg:block pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full bg-brand-accent text-text-dark-on-accent px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-accent-hover transition-colors shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0 disabled:transform-none disabled:shadow-none"
                >
                  {isSubmitting ? 'Processing...' : 'Confirm & Pay'}
                </button>
                <p className="text-xs text-text-secondary mt-3 text-center">
                  Your payment will be processed securely. You'll receive a confirmation email immediately.
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Booking Summary Sidebar (Desktop Only) */}
        <div className="hidden lg:block lg:w-1/3 w-full mt-8 lg:mt-0">
          <BookingSummary />
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 lg:hidden z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] safe-area-bottom">
        <div className="container mx-auto max-w-md flex gap-4 items-center justify-between">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Total</span>
            <span className="text-xl font-bold text-text-dark-on-accent">
              {formatCurrency(state.hasDiscount ? state.finalTotal : state.total)}
            </span>
            <span className="text-[10px] text-gray-400">Include taxes</span>
          </div>
          <button 
            onClick={handleSubmit} 
            disabled={!isFormValid || isSubmitting}
            className="bg-brand-accent text-white px-8 py-3 rounded-xl font-bold text-base shadow-lg active:scale-95 transition-transform flex-grow max-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {isSubmitting ? '...' : 'Pay Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Booking Summary Component
// const BookingSummary = () => {
//   const { state, formatCurrency, formatDisplayDate, calculateNights } = useBooking();
//   const nights = calculateNights();

//   const totalRoomsInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);

//   // Determine if we should show discounted prices (only for manually applied promos, not widget)
//   const showDiscountedPrices = state.hasDiscount && !state.fromWidget && state.discountedSubtotal;

//   const getGroupedRooms = () => {
//     const groups = {};
//     state.cart.forEach((item) => {
//       const key = item.roomId || item.categoryId;
//       if (!groups[key]) {
//         groups[key] = {
//           ...item,
//           totalAdults: (item.adultsCount || item.guestInfo?.adults || 1) * item.quantity,
//           totalChildren: (item.childrenCount || item.guestInfo?.children || 0) * item.quantity,
//           // Use discounted price per night if available and manually applied
//           pricePerNight: showDiscountedPrices && item.discountedPricePerNight 
//             ? item.discountedPricePerNight 
//             : item.roomPrice,
//           totalPrice: (showDiscountedPrices && item.discountedPricePerNight 
//             ? item.discountedPricePerNight 
//             : item.roomPrice) * item.quantity * nights,
//           originalPrice: item.roomPrice
//         };
//       } else {
//         groups[key].quantity += item.quantity;
//         groups[key].totalAdults += (item.adultsCount || item.guestInfo?.adults || 1) * item.quantity;
//         groups[key].totalChildren += (item.childrenCount || item.guestInfo?.children || 0) * item.quantity;
//         const priceToUse = showDiscountedPrices && item.discountedPricePerNight 
//           ? item.discountedPricePerNight 
//           : item.roomPrice;
//         groups[key].totalPrice += priceToUse * item.quantity * nights;
//       }
//     });
//     return Object.values(groups);
//   };

//   const groupedRooms = getGroupedRooms();

//   // Calculate discounted values if manually applied promo
//   const discountedSubtotal = showDiscountedPrices && state.discountedSubtotal 
//     ? state.discountedSubtotal 
//     : state.subtotal;
  
//   const discountedTaxes = showDiscountedPrices && state.discountedTaxes 
//     ? state.discountedTaxes 
//     : state.taxes;
  
//   // CORRECT TOTAL = discounted subtotal + discounted taxes
//   const correctTotal = showDiscountedPrices 
//     ? (state.discountedSubtotal || 0) + (state.discountedTaxes || 0)
//     : state.total;
  
//   // Final total with discount applied (for display)
//   const finalTotal = state.hasDiscount ? state.finalTotal : state.total;

//   return (
//     <div className="lg:sticky top-24">
//       <div className="bg-white lg:shadow-md lg:rounded-xl lg:p-5 lg:border lg:border-gray-100">
        
//         <div className="mb-4 pb-4 border-b border-gray-200">
//           <h4 className="text-xs font-semibold text-text-secondary uppercase mb-3 tracking-wider">Stay Details</h4>

//           <div className="flex items-start gap-2 mb-4">
//             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-brand-accent mt-0.5">
//               <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
//             </svg>
//             <div>
//               <p className="text-sm font-bold text-text-primary leading-tight">
//                 {state.searchState.hotel || 'Selected Hotel'}
//               </p>
//             </div>
//           </div>
          
//           <div className="flex justify-between items-start mb-3">
//             <div>
//               <p className="text-[10px] text-text-secondary uppercase font-bold tracking-tight">Check-in</p>
//               <p className="text-sm font-bold text-text-primary">{formatDisplayDate(state.searchState.checkin)}</p>
//               <p className="text-[11px] text-text-secondary font-medium">From 02:00 PM</p>
//             </div>
            
//             <div className="h-10 border-l border-gray-200 mx-4 self-center"></div>
            
//             <div className="text-right">
//               <p className="text-[10px] text-text-secondary uppercase font-bold tracking-tight">Check-out</p>
//               <p className="text-sm font-bold text-text-primary">{formatDisplayDate(state.searchState.checkout)}</p>
//               <p className="text-[11px] text-text-secondary font-medium">By 11:00 AM</p>
//             </div>
//           </div>
          
//           <div className="flex justify-between text-[11px] font-bold text-text-primary bg-gray-50 p-2.5 rounded-lg border border-gray-100">
//             <div className="flex items-center gap-1">
//               <span className="text-brand-primary">●</span>
//               <span>{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
//             </div>
//             <span>{totalRoomsInCart} Room(s)</span>
//             <span>{state.searchState.guestCount} Guest(s)</span>
//           </div>
//         </div>

//         <div className="mb-4 pb-4 border-b border-gray-200">
//           <h4 className="text-xs font-semibold text-text-secondary uppercase mb-3 tracking-wider">Selected Rooms</h4>
//           <div className="space-y-3">
//             {groupedRooms.map((group, index) => (
//               <div key={index} className="flex justify-between items-center text-sm">
//                 <div className="text-text-primary">
//                   <span className="font-bold">{group.quantity} x {group.roomName}</span>
//                   <p className="text-[11px] text-text-secondary">
//                     {group.totalAdults} Adult(s), {group.totalChildren} Child(ren)
//                   </p>
//                   {/* Show strikethrough price if discounted */}
//                   {showDiscountedPrices && group.originalPrice !== group.pricePerNight && (
//                     <p className="text-[10px] text-gray-400 line-through">
//                       {formatCurrency(group.originalPrice * group.quantity * nights)}
//                     </p>
//                   )}
//                 </div>
//                 <div className="font-bold text-text-primary">
//                   {formatCurrency(group.totalPrice)}
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>

//         <div className="space-y-2">
//           <div className="flex justify-between text-sm">
//             <span className="font-bold text-text-primary">Tariff for {nights} Night(s)</span>
//             <span className="font-semibold text-text-primary">
//               {formatCurrency(showDiscountedPrices ? discountedSubtotal : state.subtotal)}
//             </span>
//           </div>
//           <div className="flex justify-between text-sm">
//             <span className="text-text-secondary">Taxes</span>
//             <span className="font-semibold text-text-primary">
//               {formatCurrency(showDiscountedPrices ? discountedTaxes : state.taxes)}
//             </span>
//           </div>
//           {state.hasDiscount && !state.fromWidget && (
//             <div className="flex justify-between text-sm text-green-600">
//               <span className="font-semibold">Discount</span>
//               <span className="font-semibold">- {formatCurrency(state.discountAmount)}</span>
//             </div>
//           )}
//           <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-gray-100">
//             <span className="text-base font-bold text-text-primary">Total Payable</span>
//             <span className="text-xl font-bold text-text-primary">
//               {formatCurrency(showDiscountedPrices ? correctTotal : state.total)}
//             </span>
//           </div>
          
//           {/* Show savings for manual promos */}
//           {showDiscountedPrices && (
//             <div className="text-xs text-green-600 text-right mt-1">
//               You save {formatCurrency(state.discountAmount)}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// Booking Summary Component - FIXED VERSION
const BookingSummary = () => {
  const { state, formatCurrency, formatDisplayDate, calculateNights } = useBooking();
  const nights = calculateNights();

  const totalRoomsInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);

  // Determine if we should show discounted prices
  const showDiscountedPrices = state.hasDiscount && !state.fromWidget && state.discountedSubtotal;

  // CRITICAL: Force recalculate discounted taxes from promoCodeInfo if available
  let effectiveDiscountedSubtotal = state.discountedSubtotal;
  let effectiveDiscountedTaxes = state.discountedTaxes;
  
  // If promoCodeInfo has the discounted charges, use those directly
  if (showDiscountedPrices && state.promoCodeInfo?.discounted_charges) {
    effectiveDiscountedSubtotal = state.promoCodeInfo.discounted_charges.subtotal;
    effectiveDiscountedTaxes = state.promoCodeInfo.discounted_charges.taxes?.total_tax;
  }
  
  // If still no taxes, calculate from cart items
  if (showDiscountedPrices && (!effectiveDiscountedTaxes || effectiveDiscountedTaxes === 0)) {
    // Calculate taxes from updated cart items
    let totalTax = 0;
    state.cart.forEach(item => {
      if (item.discountedPricePerNight) {
        const roomTotal = item.discountedPricePerNight * item.quantity * nights;
        // Assuming 18% tax rate
        totalTax += roomTotal * 0.18;
      } else if (item.taxData) {
        totalTax += (item.taxData.total_cgst_amount_after_tax || 0) +
                   (item.taxData.total_sgst_amount_after_tax || 0) +
                   (item.taxData.total_cess_amount || 0);
      }
    });
    effectiveDiscountedTaxes = totalTax;
  }

  const getGroupedRooms = () => {
    const groups = {};
    state.cart.forEach((item) => {
      const key = item.roomId || item.categoryId;
      if (!groups[key]) {
        groups[key] = {
          ...item,
          totalAdults: (item.adultsCount || item.guestInfo?.adults || 1) * item.quantity,
          totalChildren: (item.childrenCount || item.guestInfo?.children || 0) * item.quantity,
          // Use discounted price per night if available
          pricePerNight: showDiscountedPrices && item.discountedPricePerNight 
            ? item.discountedPricePerNight 
            : item.roomPrice,
          totalPrice: (showDiscountedPrices && item.discountedPricePerNight 
            ? item.discountedPricePerNight 
            : item.roomPrice) * item.quantity * nights,
          originalPrice: item.originalPrice || item.roomPrice
        };
      } else {
        groups[key].quantity += item.quantity;
        groups[key].totalAdults += (item.adultsCount || item.guestInfo?.adults || 1) * item.quantity;
        groups[key].totalChildren += (item.childrenCount || item.guestInfo?.children || 0) * item.quantity;
        const priceToUse = showDiscountedPrices && item.discountedPricePerNight 
          ? item.discountedPricePerNight 
          : item.roomPrice;
        groups[key].totalPrice += priceToUse * item.quantity * nights;
      }
    });
    return Object.values(groups);
  };

  const groupedRooms = getGroupedRooms();

  // Calculate totals
  const originalSubtotal = Math.round(state.subtotal || 0);
  const originalTaxes = Math.round(state.taxes || 0);
  const originalTotal = originalSubtotal + originalTaxes;
  
  // Use effective discounted values
  const discountedSubtotal = showDiscountedPrices ? Math.round(effectiveDiscountedSubtotal || 0) : originalSubtotal;
  const discountedTaxes = showDiscountedPrices ? Math.round(effectiveDiscountedTaxes || 0) : originalTaxes;
  
  let finalTotal;
  if (showDiscountedPrices) {
    if (state.finalTotal) {
      finalTotal = Math.round(state.finalTotal);
    } else if (state.discountedTotal) {
      finalTotal = Math.round(state.discountedTotal);
    } else {
      finalTotal = discountedSubtotal + discountedTaxes;
    }
  } else {
    finalTotal = originalTotal;
  }
  
  finalTotal = Math.round(finalTotal);
  const discountedTotal = finalTotal;
  const discountAmount = showDiscountedPrices ? Math.round(state.discountAmount || 0) : 0;
 // Get the final payable amount for the widget
  const finalPayableAmount = finalTotal; // This should be the total after all discounts
  return (
    <div className="lg:sticky top-24">
      <div className="bg-white lg:shadow-md lg:rounded-xl lg:p-5 lg:border lg:border-gray-100">
        
        <div className="mb-4 pb-4 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-text-secondary uppercase mb-3 tracking-wider">Stay Details</h4>

          <div className="flex items-start gap-2 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-brand-accent mt-0.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
            </svg>
            <div>
              <p className="text-sm font-bold text-text-primary leading-tight">
                {state.searchState.hotel || 'Selected Hotel'}
              </p>
            </div>
          </div>
          
          <div className="flex justify-between items-start mb-3">
            <div>
              <p className="text-[10px] text-text-secondary uppercase font-bold tracking-tight">Check-in</p>
              <p className="text-sm font-bold text-text-primary">{formatDisplayDate(state.searchState.checkin)}</p>
              <p className="text-[11px] text-text-secondary font-medium">From 02:00 PM</p>
            </div>
            
            <div className="h-10 border-l border-gray-200 mx-4 self-center"></div>
            
            <div className="text-right">
              <p className="text-[10px] text-text-secondary uppercase font-bold tracking-tight">Check-out</p>
              <p className="text-sm font-bold text-text-primary">{formatDisplayDate(state.searchState.checkout)}</p>
              <p className="text-[11px] text-text-secondary font-medium">By 11:00 AM</p>
            </div>
          </div>
          
          <div className="flex justify-between text-[11px] font-bold text-text-primary bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <div className="flex items-center gap-1">
              <span className="text-brand-primary">●</span>
              <span>{nights} {nights === 1 ? 'Night' : 'Nights'}</span>
            </div>
            <span>{totalRoomsInCart} Room(s)</span>
            <span>{state.searchState.guestCount} Guest(s)</span>
          </div>
        </div>

        <div className="mb-4 pb-4 border-b border-gray-200">
          <h4 className="text-xs font-semibold text-text-secondary uppercase mb-3 tracking-wider">Selected Rooms</h4>
          <div className="space-y-3">
            {groupedRooms.map((group, index) => {
              const originalRoomTotal = group.originalPrice * group.quantity * nights;
              const currentRoomTotal = group.totalPrice;
              
              return (
                <div key={index} className="flex justify-between items-center text-sm">
                  <div className="text-text-primary">
                    <span className="font-bold">{group.quantity} x {group.roomName}</span>
                    <p className="text-[11px] text-text-secondary">
                      {group.totalAdults} Adult(s), {group.totalChildren} Child(ren)
                    </p>
                    {showDiscountedPrices && originalRoomTotal > currentRoomTotal && (
                      <p className="text-[10px] text-gray-400 line-through">
                        {formatCurrency(originalRoomTotal)}
                      </p>
                    )}
                  </div>
                  <div className="font-bold text-text-primary">
                    {formatCurrency(currentRoomTotal)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-2"> 
          <div className="flex justify-between text-sm">
            <span className="font-bold text-text-primary">Tariff for {nights} Night(s)</span>
            <span className="font-semibold text-text-primary">
              {formatCurrency(discountedSubtotal)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Taxes</span>
            <span className="font-semibold text-text-primary">
              {formatCurrency(discountedTaxes)}
            </span>
          </div>
                    
          <div className="flex justify-between items-center pt-3 mt-2 border-t-2 border-gray-100">
            <span className="text-base font-bold text-text-primary">Total Payable</span>
            <span className="text-xl font-bold text-text-primary">
              {formatCurrency(discountedTotal)}
            </span>
          </div>
          
          {showDiscountedPrices && discountAmount > 0 && (
            <div className="text-xs text-green-600 text-right mt-2 bg-green-50 p-2 rounded-lg">
              <span className="font-semibold">You save {formatCurrency(discountAmount)}</span>
              {state.promoCode && (
                <span className="block text-[10px]">with promo code: {state.promoCode}</span>
              )}
            </div>
          )}
        </div>
         {/* Add CCAvenue Widget here - after all pricing information */}
        <CCAvenueWidget amount={finalPayableAmount} />
      </div>
    </div>
  );
};

export default Booking;